/** @jsxImportSource @emotion/react */
import { useLazyQuery, useQuery } from '@apollo/client'
import React, { useMemo, useState } from 'react'
import { Button } from '@mui/material'
import { format } from 'date-fns-tz'
import { subDays } from 'date-fns'
import { DentalSchoolsEnum } from '__generatedTypes__/globalTypes'
import { exportInPersonMockStats, exportInPersonMockStatsVariables } from '__generatedTypes__/exportInPersonMockStats'
import { findInPersonMockResults, findInPersonMockResultsVariables } from '__generatedTypes__/findInPersonMockResults'
import { EXPORT_IN_PERSON_MOCK_RESULTS, FIND_IN_PERSON_MOCK_RESULTS } from 'queries/practiceTest'
import {
  findSchoolListByPlatform,
  findSchoolListByPlatformVariables,
} from '__generatedTypes__/findSchoolListByPlatform'
import { FIND_SCHOOL_LIST_BY_PLATFORM } from 'queries/faculty'
import Table from 'components/Table'
import DateTimeInput from 'components/DateTimeInput'
import { saveBase64AsExcelFile } from 'utils/saveFile'
import { toast } from 'utils/toast'
import * as classes from './styles'

export type InPersonMockResultsProps = {
  platformId: string
  practiceTestId: string
}

export const InPersonMockResults = ({ platformId, practiceTestId }: InPersonMockResultsProps) => {
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const date = subDays(new Date(), 14)
    date.setHours(0, 0, 0, 0)
    return date
  })
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const date = new Date()
    date.setHours(23, 59, 59, 999)
    return date
  })
  const [isExporting, setIsExporting] = useState(false)

  const [exportInPersonMockResults] = useLazyQuery<exportInPersonMockStats, exportInPersonMockStatsVariables>(
    EXPORT_IN_PERSON_MOCK_RESULTS,
    {
      fetchPolicy: 'network-only',
    }
  )

  const schoolsState = useQuery<findSchoolListByPlatform, findSchoolListByPlatformVariables>(
    FIND_SCHOOL_LIST_BY_PLATFORM,
    {
      variables: {
        platformId,
      },
      fetchPolicy: 'cache-and-network',
    }
  )
  const universities = useMemo(
    () =>
      schoolsState.data?.findSchoolListByPlatform?.schoolArray?.reduce(
        (acc, school) => ({
          ...acc,
          [school.key]: school.value,
        }),
        {} as Record<string, string>
      ) ?? {},
    [schoolsState.data?.findSchoolListByPlatform]
  )

  const { data, loading } = useQuery<findInPersonMockResults, findInPersonMockResultsVariables>(
    FIND_IN_PERSON_MOCK_RESULTS,
    {
      variables: {
        query: {
          practiceTest: {
            id: practiceTestId,
          },
          createdAt: {
            from: startDate ? format(startDate, "yyyy-MM-dd'T00:00:00.000'") : undefined,
            to: endDate ? format(endDate, "yyyy-MM-dd'T23:59:59.999'") : undefined,
          },
        },
      },
      fetchPolicy: 'network-only',
    }
  )

  const items = data?.findInPersonMockResults.map((pt) => ({ id: pt.ptId, ...pt })) ?? []

  const handleExportResults = (ptId: string, university: DentalSchoolsEnum) => async () => {
    if (isExporting) {
      return
    }

    try {
      setIsExporting(true)
      const { data } = await exportInPersonMockResults({
        variables: {
          query: {
            practiceTest: {
              id: ptId,
            },
            university,
            createdAt: {
              from: startDate ? format(startDate, "yyyy-MM-dd'T00:00:00.000'") : undefined,
              to: endDate ? format(endDate, "yyyy-MM-dd'T23:59:59.999'") : undefined,
            },
          },
        },
      })

      if (!data?.exportInPersonMockStats) {
        return
      }

      const startDateFormatted = startDate ? format(startDate, 'yyyy-MM-dd') : undefined
      const endDateFormatted = endDate ? format(endDate, 'yyyy-MM-dd') : undefined

      const dateRange =
        !startDateFormatted && !endDateFormatted
          ? ''
          : startDateFormatted === endDateFormatted
          ? ` (${startDateFormatted})`
          : !endDateFormatted
          ? ` (from ${startDateFormatted})`
          : !startDateFormatted
          ? ` (until ${endDateFormatted})`
          : ` (${startDateFormatted} - ${endDateFormatted})`

      const fileName = `${universities[university] ?? university} - In-Person Mock Results${dateRange}.xlsx`
      saveBase64AsExcelFile(data.exportInPersonMockStats, fileName)
      toast.success('Results exported successfully')
    } catch (error) {
      toast.error('Failed to export results')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div>
      <div css={classes.filtersWrapper}>
        <DateTimeInput
          value={startDate}
          onChange={(value) => setStartDate(value)}
          label="Start Date"
          placeholder="Select Start Date"
          pickerType="date"
          format="MMMM d, yyyy"
          shouldDisableDate={(date) => !!endDate && date > endDate}
          withConfirmation={false}
          withClearIcon
          fullWidth
        />
        <DateTimeInput
          value={endDate}
          onChange={(value) => setEndDate(value)}
          label="End Date"
          placeholder="Select End Date"
          pickerType="date"
          format="MMMM d, yyyy"
          withConfirmation={false}
          shouldDisableDate={(date) => !!startDate && date < startDate}
          withClearIcon
          fullWidth
        />
      </div>
      <Table
        columns={[
          {
            title: 'Title',
            renderCell: ({ ptTitle }) => ptTitle,
            flex: 1,
          },
          {
            title: 'University',
            renderCell: ({ university }) => String(universities[university] ?? university),
            flex: 1,
          },
          {
            title: '# Students',
            renderCell: ({ userCount }) => String(userCount),
            flex: 1,
          },
          {
            title: 'Download Report',
            renderCell: ({ ptId, university }) => (
              <Button onClick={handleExportResults(ptId, university)} size="small" disabled={isExporting}>
                Download
              </Button>
            ),
            flex: 1,
          },
        ]}
        items={items}
        isSortable={false}
        isNotLoaded={schoolsState.loading || loading}
      />
    </div>
  )
}
